import React, { useMemo, useRef } from "react";
import { gql, useQuery } from "urql";
import * as Popover from "@radix-ui/react-popover";
import { saveAs } from "file-saver";
import { groupBy, orderBy, round, sortBy, sortedLastIndex, sumBy, uniq } from "lodash";
import { FileSpreadsheet, ImageDown, Info } from "lucide-react";

import { DateHelper } from "../../../common/src";
import { twoDecPriceFormat } from "../../../common/src/utils/FormatFunctions";

import { COLORWAY } from "../../../components/src/charts/chartTheme";
import { TimeSeriesLines } from "../../../components/src/charts/TimeSeriesLines";
import { exportToXlsx } from "../../../components/src/common/exportToXlsx";
import { ReactTable } from "../../../components/src/react-table/ReactTable";
import { useQueryState } from "../../../components/src/use-query-state";

import { aumSegmentByClientAccount, aumSegments } from "./aumSegments";

import type { NamedSeries } from "../../../components/src/charts/chartTheme";

import "./AumQuarterlyPage.css";

const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];

// A date written as e.g. "Tue 13 Oct", for the headers of the last week and yesterday
const dayFormat = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });

// start is the baseline date (the day before the period begins), end is the last day of the period. Both are YYYY-MM-DD
interface Period {
    key: string;
    label: string;
    start: string;
    end: string;
}

const gfiFundId = "649d36255de897ec4079bbae";
export const perenneFundId = "649d4a855de897ec4079d879";

// Order and grouping of the fund rows, by fund id. Funds that are not listed come last, in a group of their own
const fundGroups = [
    [
        "605b2e5cc34cf5001154c90d", // Aster GC
        "62690582071ef0776524606c", // Aster HY
        "62690a20071ef07765246144", // Aster ST
        "5b0d638cafcedd32f03a8ac7", // Dahlia
        "58e64b9523d2772e1859b705", // Iris
        "594a2cf723d2773014ec27b7", // Scilla
        "684a93694509026c2bdb2e3a" // Factoring
    ],
    [gfiFundId, perenneFundId]
];

// The funds that are sold through Avanza
const avanzaFundIds = [gfiFundId, perenneFundId];

// Index is the number of decimals for numbers below 1: none for the fund overview, one for a segment and two for a single customer, whose
// holdings can be small
const numberFormats = [0, 1, 2].map(
    (digits) => new Intl.NumberFormat("sv-SE", { minimumFractionDigits: digits, maximumFractionDigits: digits })
);

// The segment option that shows every fund, the segment of the client accounts that are not in aumSegmentByClientAccount, and the customer
// option that shows the whole segment
const fundOverview = "Fund overview";
const unmappedSegment = "Unmapped";
const allCustomers = "All customers";
const segmentOptions = [...aumSegments, unmappedSegment];

export const getNavs = gql`
    query getNavs($startDate: GraphQLDateString!) {
        navs(startDate: $startDate, statusIn: [Active, Locked]) {
            clientId
            client {
                name
            }
            date
            status
            data {
                classes {
                    instrument {
                        isin
                    }
                    navPerUnit
                    unitsBeforeOrders
                    unitsAfterOrders
                    navAfterAdjustmentsAfterOrders
                    fxRate
                }
            }
        }
    }
`;

interface FundNav {
    clientId: string;
    date: string;
    status: string;
    data: {
        classes: {
            instrument?: { isin: string };
            navAfterAdjustmentsAfterOrders: string;
            unitsAfterOrders: string;
            unitsBeforeOrders: string;
            navPerUnit: string;
            fxRate: string;
        }[];
    };
}

// The NAVs of a fund, one per date (a locked NAV before an active one), with the AUM and the flow of every date. AUM is the NAV after orders.
// The flow is the units subscribed or redeemed on the NAV date, valued at the NAV per unit. Both in MSEK
const getFundNavSeries = (navs: FundNav[], fundId: string) => {
    const navsByDate: Record<string, FundNav> = {};
    for (const nav of navs) {
        if (nav.clientId === fundId && (!navsByDate[nav.date] || nav.status === "Locked")) navsByDate[nav.date] = nav;
    }
    const dates = Object.keys(navsByDate).sort();

    const aums = dates.map((date) =>
        sumBy(navsByDate[date].data.classes, (c) => (Number(c.navAfterAdjustmentsAfterOrders) * Number(c.fxRate)) / 1000000.0)
    );
    const flows = dates.map((date) =>
        sumBy(
            navsByDate[date].data.classes,
            (c) => ((Number(c.unitsAfterOrders) - Number(c.unitsBeforeOrders)) * Number(c.navPerUnit) * Number(c.fxRate)) / 1000000.0
        )
    );
    return { navsByDate, dates, aums, flows };
};

// Listed funds in the order of fundGroups and then the others by name
const sortFunds = <T extends { _id: string; name: string }>(funds: T[]): T[] => {
    const listedFundIds = fundGroups.flat();
    return sortBy(funds, [
        (fund) => {
            const index = listedFundIds.indexOf(fund._id);
            return index === -1 ? listedFundIds.length : index;
        },
        "name"
    ]);
};

// The chart starts when the first fund (Iris) started
const firstNavDate = "2017-01-01";

// Every NAV from firstNavDate, with only what the AUM chart needs. It is a long history, so it is not fetched together with the table's NAVs
const getNavsForChart = gql`
    query getNavsForChart($startDate: GraphQLDateString!) {
        navs(startDate: $startDate, statusIn: [Active, Locked]) {
            clientId
            client {
                name
            }
            date
            status
            data {
                classes {
                    navAfterAdjustmentsAfterOrders
                    unitsBeforeOrders
                    unitsAfterOrders
                    navPerUnit
                    fxRate
                }
            }
        }
    }
`;

// The list of the latest subscriptions and redemptions has the orders of the last ordersDays days up to the end date. The orders of all accounts
// are fetched, so that changing segment or customer does not fetch again. The server leaves out deleted orders, and the page leaves out account
// switches, which are not real subscriptions or redemptions
const ordersDays = 30;
const getOrdersInPeriod = gql`
    query getOrdersInPeriod($fromDate: GraphQLDateString!, $toDate: GraphQLDateString!) {
        transferAgentOrders(filter: { tradeDateStart: $fromDate, tradeDateEnd: $toDate }) {
            _id
            tradeDate
            subType
            transactionType
            currency
            amount
            units
            settledAmount
            isin
            clientAccount
            clientName
            clientId
            status
            createTimestamp
        }
    }
`;

export const AumQuarterlyPage = (): React.ReactElement => {
    const todaysDate = DateHelper.dateToString(DateHelper.today()).substring(0, 10);

    const [endDate, setEndDate] = useQueryState("endDate", todaysDate);
    const [segment, setSegment] = useQueryState("segment", fundOverview);
    const [customer, setCustomer] = useQueryState("customer", allCustomers);

    // Anything that is not a known segment shows the fund overview
    const isOverview = !segmentOptions.includes(segment);

    const containerRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);

    // Always the same four periods, from left to right: the current year to date and the current month, both ending on the end date, and two
    // single days: the same weekday a week ago and the day before the end date
    const periods = useMemo((): Period[] => {
        const year = Number(endDate.substring(0, 4));
        const month = Number(endDate.substring(5, 7));
        // Last day of a 1-based month, e.g. lastDayOfMonth(2026, 3) is 2026-03-31
        const lastDayOfMonth = (y: number, m: number): string => new Date(Date.UTC(y, m, 0)).toISOString().substring(0, 10);

        // The end date moved back by the given number of days
        const daysBefore = (days: number): string => {
            const date = new Date(endDate);
            date.setUTCDate(date.getUTCDate() - days);
            return date.toISOString().substring(0, 10);
        };

        return [
            { key: "year", label: `${year} YTD`, start: lastDayOfMonth(year - 1, 12), end: endDate },
            {
                key: "month",
                label: `${monthNames[month - 1]} ${year}`,
                start: lastDayOfMonth(month === 1 ? year - 1 : year, month === 1 ? 12 : month - 1),
                end: endDate
            },
            { key: "week", label: `Last week (${dayFormat.format(new Date(daysBefore(7)))})`, start: daysBefore(8), end: daysBefore(7) },
            { key: "day", label: `Yesterday (${dayFormat.format(new Date(daysBefore(1)))})`, start: daysBefore(2), end: daysBefore(1) }
        ];
    }, [endDate]);

    // The NAVs from two weeks before the earliest baseline date, so that there is a NAV on or before it even when the NAV is not daily
    const navsStartDate = useMemo(() => {
        const date = new Date(sortBy(periods.map((period) => period.start))[0]);
        date.setUTCDate(date.getUTCDate() - 14);
        return date.toISOString().substring(0, 10);
    }, [periods]);

    const [{ data, fetching, error }] = useQuery({ query: getNavs, variables: { startDate: navsStartDate } });

    // The share register on every baseline and end date, one aliased field per distinct date, since each one is read from all orders. The flow
    // of a period is the change of the accumulated order amount between its baseline and its end date
    const { registerQuery, registerVariables } = useMemo(() => {
        const declarations: string[] = [];
        const fields: string[] = [];
        const variables: Record<string, string> = {};
        for (const date of new Set(periods.flatMap((period) => [period.start, period.end]))) {
            const alias = `d${date.replaceAll("-", "")}`;
            variables[alias] = date;
            declarations.push(`$${alias}: GraphQLDateString!`);
            fields.push(`${alias}: shareRegister(filter: { tradeDateEnd: $${alias} }) { ...registerItem }`);
        }
        const query = gql(
            `query getShareRegisters(${declarations.join(", ")}) { ${fields.join(" ")} } fragment registerItem on ShareRegisterItem { clientAccount customer { clientAccountLabel } isin units amount fundId }`
        );
        return { registerQuery: query, registerVariables: variables };
    }, [periods]);

    // The share register is only needed when a segment is selected
    const [{ data: registerData, fetching: fetchingRegister, error: errorRegister }] = useQuery({
        query: registerQuery,
        variables: registerVariables,
        pause: isOverview
    });

    // The customers (client accounts) of the selected segment that hold units on any of the dates, by name and account number.
    // The account number tells apart accounts that have the same name
    const customers = useMemo((): { clientAccount: string; label: string }[] => {
        if (isOverview || !registerData) return [];

        const customersByAccount: Record<string, { clientAccount: string; label: string }> = {};
        for (const alias of Object.keys(registerVariables)) {
            for (const item of registerData[alias] ?? []) {
                if ((aumSegmentByClientAccount[item.clientAccount]?.segment ?? unmappedSegment) === segment && Number(item.units) !== 0) {
                    customersByAccount[item.clientAccount] = {
                        clientAccount: item.clientAccount,
                        label: `${item.customer?.clientAccountLabel ?? "Unknown"} (${item.clientAccount})`
                    };
                }
            }
        }
        return sortBy(Object.values(customersByAccount), "label");
    }, [isOverview, registerData, registerVariables, segment]);

    // A customer from the URL that is not in the selected segment shows the whole segment
    const selectedCustomer = customers.some((option) => option.clientAccount === customer) ? customer : allCustomers;

    let decimals = 0;
    if (!isOverview) decimals = selectedCustomer === allCustomers ? 1 : 2;

    // The client accounts that hold units on any of the dates but are not in aumSegmentByClientAccount, and their AUM on the end date in MSEK
    const unmappedSummary = useMemo(() => {
        if (isOverview || !registerData || !data) return { count: 0, aum: 0 };

        const unmappedAccounts = new Set<string>();
        for (const alias of Object.keys(registerVariables)) {
            for (const item of registerData[alias] ?? []) {
                if (!aumSegmentByClientAccount[item.clientAccount] && Number(item.units) !== 0) unmappedAccounts.add(item.clientAccount);
            }
        }

        const navsByFund = groupBy(data.navs, "clientId");
        let aum = 0;
        for (const item of registerData[`d${endDate.replaceAll("-", "")}`] ?? []) {
            if (aumSegmentByClientAccount[item.clientAccount]) continue;

            // The last NAV on or before the end date, a locked NAV before an active one on the same date
            const navs = sortBy(
                (navsByFund[item.fundId] ?? []).filter((nav: { date: string }) => nav.date <= endDate),
                ["date", (nav: { status: string }) => (nav.status === "Locked" ? 1 : 0)]
            );
            const navClass = navs[navs.length - 1]?.data.classes.find(
                (c: { instrument?: { isin: string } }) => c.instrument?.isin === item.isin
            );
            if (navClass) aum += (Number(item.units) * Number(navClass.navPerUnit) * Number(navClass.fxRate)) / 1000000.0;
        }
        return { count: unmappedAccounts.size, aum };
    }, [isOverview, registerData, registerVariables, data, endDate]);

    // The funds that have a NAV
    const funds: { _id: string; name: string }[] = useMemo(() => {
        const fundsById: Record<string, { _id: string; name: string }> = {};
        for (const nav of data?.navs ?? []) {
            fundsById[nav.clientId] = { _id: nav.clientId, name: nav.client?.name ?? nav.clientId };
        }
        return sortFunds(Object.values(fundsById));
    }, [data]);

    // The long NAV history for the chart is fetched after the table's NAVs, so that it does not slow the table down
    const [{ data: chartData, fetching: fetchingChart, error: errorChart }] = useQuery({
        query: getNavsForChart,
        variables: { startDate: firstNavDate },
        pause: !isOverview || !data
    });

    // AUM per fund, the total AUM and the accumulated flow per NAV date from the first NAV to the end date, in MSEK. The total carries a fund's
    // last NAV forward over days without a NAV, except for a closed fund (no NAV in the 45 days before the end date, which is longer than the time
    // between the NAVs of a fund that does not have a daily NAV), which ends with its last NAV
    const chartSeries = useMemo((): NamedSeries[] => {
        if (!chartData || !isOverview) return [];

        const fundsById: Record<string, { _id: string; name: string }> = {};
        for (const nav of chartData.navs) {
            fundsById[nav.clientId] = { _id: nav.clientId, name: nav.client?.name ?? nav.clientId };
        }

        const openFrom = new Date(endDate);
        openFrom.setUTCDate(openFrom.getUTCDate() - 45);
        const openFromDate = openFrom.toISOString().substring(0, 10);

        const fundSeries = sortFunds(Object.values(fundsById))
            .map((fund) => {
                const series = getFundNavSeries(chartData.navs, fund._id);
                const count = sortedLastIndex(series.dates, endDate);
                return {
                    name: fund.name,
                    dates: series.dates.slice(0, count),
                    aums: series.aums.slice(0, count),
                    flows: series.flows.slice(0, count)
                };
            })
            .filter((series) => series.dates.length > 0);

        const aumPoints: NamedSeries["points"] = [];
        const flowPoints: NamedSeries["points"] = [];
        let accumulatedFlow = 0;
        for (const date of sortBy(uniq(fundSeries.flatMap((series) => series.dates)))) {
            let aum = 0;
            for (const series of fundSeries) {
                const index = sortedLastIndex(series.dates, date) - 1;
                if (index < 0) continue;
                const closed = series.dates[series.dates.length - 1] < openFromDate;
                if (closed && date > series.dates[series.dates.length - 1]) continue;
                aum += series.aums[index];
                // The flow is only of a NAV on the date
                if (series.dates[index] === date) accumulatedFlow += series.flows[index];
            }
            aumPoints.push({ date: new Date(date), value: aum });
            flowPoints.push({ date: new Date(date), value: accumulatedFlow });
        }

        // The colours are set here so that the legend below the chart, which wraps, has the same colours as the lines
        return [
            ...fundSeries.map((series) => ({
                name: series.name,
                points: series.dates.map((date, index) => ({ date: new Date(date), value: series.aums[index] }))
            })),
            { name: "Total AUM", points: aumPoints },
            { name: "Flows", points: flowPoints, dash: true }
        ].map((series, index) => ({ ...series, color: COLORWAY[index % COLORWAY.length] }));
    }, [chartData, isOverview, endDate]);

    const columns = useMemo(
        () => [
            {
                id: "name",
                header: "MSEK",
                accessorKey: "name",
                size: 150,
                enableSorting: false,
                enableColumnFilter: false,
                meta: { className: "aum-col-name" },
                cell: (info: { getValue: () => string; row: { original: { rowClassName: string } } }) => (
                    <span className={info.row.original.rowClassName}>{info.getValue()}</span>
                )
            },
            ...periods.map((period, index) => {
                // The periods alternate between white and a light tint
                const periodClassName = index % 2 === 1 ? "aum-band" : "";
                return {
                    id: period.key,
                    header: period.label,
                    enableSorting: false,
                    enableColumnFilter: false,
                    meta: { className: `aum-period-head ${periodClassName}` },
                    columns: [
                        ["valueChange", "Value chg.", 90],
                        ["flow", "Flow", 66],
                        // Only the last period shows the AUM, the others show the sum of value change and flow
                        index === periods.length - 1 ? ["aum", "AUM", 78] : ["total", "Total chg.", 78]
                    ].map(([field, header, size]) => ({
                        id: `${period.key}_${field}`,
                        header,
                        size: size as number,
                        accessorKey: `${period.key}_${field}`,
                        enableSorting: false,
                        enableColumnFilter: false,
                        meta: {
                            className: `${periodClassName} aum-col-num aum-col-${field === "total" ? "aum" : field}${field === "valueChange" ? " aum-period-start" : ""}`
                        },
                        cell: (info: { getValue: () => number }) => {
                            // Numbers of at least 1 (and exact zeros) never get decimals, only the small ones do
                            const value = info.getValue();
                            let format = numberFormats[decimals];
                            if (Math.abs(value) >= 1 || value === 0) format = numberFormats[0];
                            return <span className={value < 0 ? "aum-negative" : undefined}>{format.format(value)}</span>;
                        }
                    }))
                };
            })
        ],
        [periods, decimals]
    );

    // One row per fund with value change, flow and AUM in MSEK for every period
    const rows = useMemo(() => {
        if (!data) return [];
        if (!isOverview && !registerData) return [];

        // The share register items of the selected segment (and customer), per date alias
        const segmentItems: Record<string, { fundId: string; isin: string; units: string; amount: string }[]> = {};
        const segmentFundIds = new Set<string>();
        if (!isOverview) {
            for (const alias of Object.keys(registerVariables)) {
                segmentItems[alias] = (registerData[alias] ?? []).filter(
                    (item: { clientAccount: string }) =>
                        (aumSegmentByClientAccount[item.clientAccount]?.segment ?? unmappedSegment) === segment &&
                        (selectedCustomer === allCustomers || item.clientAccount === selectedCustomer)
                );
                segmentItems[alias].forEach((item) => {
                    if (Number(item.units) !== 0) segmentFundIds.add(item.fundId);
                });
            }
        }

        // With a segment selected, only the funds that the segment has holdings or orders in are listed
        let visibleFunds = funds;
        if (!isOverview) visibleFunds = funds.filter((fund) => segmentFundIds.has(fund._id));

        // A separator line is drawn where the group changes
        const groupIndexes = visibleFunds.map((fund) => {
            const index = fundGroups.findIndex((group) => group.includes(fund._id));
            return index === -1 ? fundGroups.length : index;
        });

        const fundRows = visibleFunds.map((fund, fundIndex) => {
            const row: Record<string, string | number> = {
                id: fund._id,
                name: fund.name,
                rowClassName: fundIndex > 0 && groupIndexes[fundIndex] !== groupIndexes[fundIndex - 1] ? "aum-row-separator" : ""
            };

            const { navsByDate, dates, aums, flows } = getFundNavSeries(data.navs, fund._id);

            // AUM on the last NAV date on or before the given date, 0 before the first NAV
            const aumAt = (date: string): number => {
                const index = sortedLastIndex(dates, date) - 1;
                if (index < 0) return 0;
                return aums[index];
            };

            // The NAV class with the given ISIN on the last NAV date on or before the given date
            const navClassAt = (isin: string, date: string) => {
                const index = sortedLastIndex(dates, date) - 1;
                if (index < 0) return undefined;
                return navsByDate[dates[index]].data.classes.find((c: { instrument?: { isin: string } }) => c.instrument?.isin === isin);
            };

            // The segment's AUM is its units valued at the NAV per unit. Its flow is the settled order amount, converted at the NAV's fx rate. Both in MSEK
            const unitsValueAt = (items: { isin: string; units: string }[], date: string): number =>
                sumBy(items, (item) => {
                    const navClass = navClassAt(item.isin, date);
                    if (!navClass) return 0;
                    return (Number(item.units) * Number(navClass.navPerUnit) * Number(navClass.fxRate)) / 1000000.0;
                });
            const amountAt = (items: { isin: string; amount: string }[], date: string): number =>
                sumBy(items, (item) => {
                    const navClass = navClassAt(item.isin, date);
                    if (!navClass) return 0;
                    return (Number(item.amount) * Number(navClass.fxRate)) / 1000000.0;
                });

            for (const period of periods) {
                let flow = 0;
                let aum = 0;
                let aumStart = 0;
                if (isOverview) {
                    dates.forEach((date, index) => {
                        if (date > period.start && date <= period.end) flow += flows[index];
                    });
                    aum = aumAt(period.end);
                    aumStart = aumAt(period.start);
                } else {
                    const startItems = segmentItems[`d${period.start.replaceAll("-", "")}`].filter((item) => item.fundId === fund._id);
                    const endItems = segmentItems[`d${period.end.replaceAll("-", "")}`].filter((item) => item.fundId === fund._id);
                    flow = amountAt(endItems, period.end) - amountAt(startItems, period.end);
                    aum = unitsValueAt(endItems, period.end);
                    aumStart = unitsValueAt(startItems, period.start);
                }
                row[`${period.key}_flow`] = flow;
                row[`${period.key}_aum`] = aum;
                row[`${period.key}_valueChange`] = aum - aumStart - flow;
                row[`${period.key}_total`] = aum - aumStart;
            }
            return row;
        });

        // With a segment selected only the total is shown, the Avanza breakdown is for all funds
        if (!isOverview) {
            const segmentTotal: Record<string, string | number> = { name: "Total", rowClassName: "aum-row-separator aum-row-total" };
            for (const period of periods) {
                for (const field of ["valueChange", "flow", "aum", "total"]) {
                    const column = `${period.key}_${field}`;
                    segmentTotal[column] = fundRows.reduce((sum, row) => sum + (row[column] as number), 0);
                }
            }
            return [...fundRows, segmentTotal];
        }

        // Summary rows are calculated from the unrounded fund values
        const avanzaRows = fundRows.filter((row) => avanzaFundIds.includes(row.id as string));
        const perenneRow = fundRows.find((row) => row.id === perenneFundId);
        const total: Record<string, string | number> = { name: "Total", rowClassName: "aum-row-separator aum-row-total" };
        const avanza: Record<string, string | number> = { name: "of which Avanza", rowClassName: "aum-row-separator aum-row-sub" };
        const exclAvanza: Record<string, string | number> = { name: "excl. Avanza", rowClassName: "aum-row-sub" };
        const exclPerenne: Record<string, string | number> = { name: "excl. Perenne", rowClassName: "aum-row-sub" };

        for (const period of periods) {
            for (const field of ["valueChange", "flow", "aum", "total"]) {
                const column = `${period.key}_${field}`;
                total[column] = fundRows.reduce((sum, row) => sum + (row[column] as number), 0);
                avanza[column] = avanzaRows.reduce((sum, row) => sum + (row[column] as number), 0);
                exclAvanza[column] = (total[column] as number) - (avanza[column] as number);
                exclPerenne[column] = (total[column] as number) - ((perenneRow?.[column] as number) ?? 0);
            }
        }

        return [...fundRows, total, avanza, exclAvanza, exclPerenne];
    }, [data, funds, periods, isOverview, segment, selectedCustomer, registerData, registerVariables]);

    // What the exports contain: the table as shown (in MSEK, but with four decimals instead of the rounding of the screen), the dates of the
    // periods, and the daily series of the chart when it has been loaded
    const exportSheets = useMemo(() => {
        const tableRows = rows.map((row) => {
            const exportRow: Record<string, string | number> = { Fund: row.name as string };
            periods.forEach((period, index) => {
                exportRow[`${period.label} Value chg.`] = round(row[`${period.key}_valueChange`] as number, 4);
                exportRow[`${period.label} Flow`] = round(row[`${period.key}_flow`] as number, 4);
                if (index === periods.length - 1) {
                    exportRow[`${period.label} AUM`] = round(row[`${period.key}_aum`] as number, 4);
                } else {
                    exportRow[`${period.label} Total chg.`] = round(row[`${period.key}_total`] as number, 4);
                }
            });
            return exportRow;
        });
        const periodRows = periods.map((period) => ({ Period: period.label, "Baseline date": period.start, "End date": period.end }));

        // One row per NAV date. A fund has a value on the dates it has a NAV, the total and the flow on every date
        const valuesBySeries = chartSeries.map((series) => {
            const valueByDate: Record<string, number> = {};
            for (const point of series.points) valueByDate[point.date.toISOString().substring(0, 10)] = round(point.value, 4);
            return valueByDate;
        });
        const dailyRows = sortBy(uniq(valuesBySeries.flatMap((valueByDate) => Object.keys(valueByDate)))).map((date) => {
            const dailyRow: Record<string, string | number> = { Date: date };
            chartSeries.forEach((series, index) => {
                dailyRow[series.name] = valuesBySeries[index][date] ?? "";
            });
            return dailyRow;
        });
        return { tableRows, periodRows, dailyRows };
    }, [rows, periods, chartSeries]);

    // Fetched after the table, and only in the segment views
    const ordersWindow = useMemo(() => {
        const from = new Date(endDate);
        from.setUTCDate(from.getUTCDate() - ordersDays);
        return { fromDate: from.toISOString().substring(0, 10), toDate: endDate };
    }, [endDate]);
    const [{ data: ordersData, fetching: fetchingOrders, error: errorOrders }] = useQuery({
        query: getOrdersInPeriod,
        variables: ordersWindow,
        pause: isOverview || !data
    });

    // The latest orders of the selected segment (and customer), the latest first. A settled order has a settled amount, which is used. An order
    // that is not settled and is in units has its amount estimated at the NAV per unit of the last NAV on or before the trade date, and the amount
    // in SEK uses the exchange rate of that NAV. An order that cannot be valued, since there is no NAV for it, has no amount
    const orderRows = useMemo(() => {
        if (!data || !ordersData || isOverview) return [];

        const fundNavSeries: Record<string, ReturnType<typeof getFundNavSeries>> = {};
        for (const fund of funds) fundNavSeries[fund._id] = getFundNavSeries(data.navs, fund._id);

        const orders: {
            _id: string;
            tradeDate: string;
            subType: string;
            transactionType: string;
            currency: string;
            amount: string;
            units: string;
            settledAmount: string;
            isin: string;
            clientAccount?: string;
            clientName?: string;
            clientId: string;
            status: string;
            createTimestamp: string;
        }[] = ordersData.transferAgentOrders.filter(
            (order: { clientAccount?: string; subType: string }) =>
                order.subType !== "ACCOUNT_SWITCH" &&
                (aumSegmentByClientAccount[order.clientAccount]?.segment ?? unmappedSegment) === segment &&
                (selectedCustomer === allCustomers || order.clientAccount === selectedCustomer)
        );

        const rowsOfOrders = orders.map((order) => {
            const series = fundNavSeries[order.clientId];
            const navIndex = series ? sortedLastIndex(series.dates, order.tradeDate) - 1 : -1;
            const navClass =
                navIndex >= 0
                    ? series.navsByDate[series.dates[navIndex]].data.classes.find((c) => c.instrument?.isin === order.isin)
                    : undefined;

            // The settled amount is zero until the order is settled, and it is negative for a redemption. An order is either in an amount or in units
            const settledAmount = Math.abs(Number(order.settledAmount));
            const units = Number(order.units);
            let amount: number | undefined = Number(order.amount);
            let estimated = false;
            if (settledAmount > 0) {
                amount = settledAmount;
            } else if (units > 0) {
                amount = navClass ? units * Number(navClass.navPerUnit) : undefined;
                estimated = true;
            }

            let amountSek: number | undefined;
            if (typeof amount !== "undefined") {
                if (order.currency === "SEK") amountSek = amount;
                else if (navClass) amountSek = amount * Number(navClass.fxRate);
            }

            return {
                id: order._id,
                date: order.tradeDate,
                createTimestamp: order.createTimestamp,
                customer: `${order.clientName ?? order.clientAccount} (${order.clientAccount})`,
                fund: funds.find((fund) => fund._id === order.clientId)?.name ?? order.clientId,
                type: order.transactionType === "BUY" ? "Subscription" : "Redemption",
                amount,
                estimated,
                currency: order.currency,
                amountSek,
                status: order.status
            };
        });
        return orderBy(rowsOfOrders, ["date", "createTimestamp"], ["desc", "desc"]);
    }, [data, ordersData, isOverview, segment, selectedCustomer, funds]);

    const orderColumns = useMemo(() => {
        // An amount, with a star when it is estimated, since the order is in units and not settled
        const amountCell = (info: { getValue: () => number | undefined; row: { original: { estimated: boolean } } }) => {
            const value = info.getValue();
            if (typeof value === "undefined") return <span></span>;
            return (
                <span>
                    {twoDecPriceFormat(value)}
                    {info.row.original.estimated ? " *" : ""}
                </span>
            );
        };
        return [
            { id: "date", header: "Date", accessorKey: "date", size: 90, enableSorting: true, enableColumnFilter: false },
            { id: "customer", header: "Customer", accessorKey: "customer", size: 300, enableSorting: true, enableColumnFilter: false },
            { id: "fund", header: "Fund", accessorKey: "fund", size: 220, enableSorting: true, enableColumnFilter: false },
            {
                id: "type",
                header: "Type",
                accessorKey: "type",
                size: 100,
                enableSorting: true,
                enableColumnFilter: false,
                cell: (info: { getValue: () => string }) => (
                    <span className={info.getValue() === "Redemption" ? "aum-negative" : undefined}>{info.getValue()}</span>
                )
            },
            {
                id: "amount",
                header: "Amount",
                accessorKey: "amount",
                size: 130,
                enableSorting: true,
                enableColumnFilter: false,
                meta: { className: "aum-col-num" },
                cell: amountCell
            },
            { id: "currency", header: "Currency", accessorKey: "currency", size: 70, enableSorting: true, enableColumnFilter: false },
            {
                id: "amountSek",
                header: "Amount (SEK)",
                accessorKey: "amountSek",
                size: 130,
                enableSorting: true,
                enableColumnFilter: false,
                meta: { className: "aum-col-num" },
                cell: amountCell
            },
            { id: "status", header: "Status", accessorKey: "status", size: 100, enableSorting: true, enableColumnFilter: false }
        ];
    }, []);

    let content: React.ReactElement = <ReactTable columns={columns} data={rows} />;
    if (fetching || fetchingRegister) content = <p>Loading</p>;
    if (error || errorRegister) content = <p>error: {JSON.stringify(error ?? errorRegister, null, 2)}</p>;

    let subtitle = `MSEK · Data as of ${endDate}`;
    if (!isOverview) subtitle += ` · ${segment}`;
    if (!isOverview && selectedCustomer !== allCustomers) {
        subtitle += ` · ${customers.find((option) => option.clientAccount === selectedCustomer)?.label}`;
    }

    return (
        <div className="aum-quarterly" ref={containerRef}>
            <div className="aum-quarterly-header">
                <div ref={titleRef}>
                    <h1>AUM Quarterly</h1>
                    <p>{subtitle}</p>
                </div>
                <div className="aum-quarterly-actions">
                    <label>
                        Segment
                        <select value={isOverview ? fundOverview : segment} onChange={(event) => setSegment(event.target.value)}>
                            {[fundOverview, ...segmentOptions].map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </label>
                    {!isOverview && (
                        <label>
                            Customer
                            <select value={selectedCustomer} onChange={(event) => setCustomer(event.target.value)}>
                                <option value={allCustomers}>{allCustomers}</option>
                                {customers.map((option) => (
                                    <option key={option.clientAccount} value={option.clientAccount}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                    <label>
                        End date
                        <input
                            type="date"
                            value={endDate}
                            onChange={(event) => {
                                if (event.target.value) setEndDate(event.target.value);
                            }}
                        />
                    </label>
                    {isOverview && (
                        <button
                            className="export-link"
                            type="button"
                            title="Export to Excel (the table, the periods and the daily AUM)"
                            onClick={() => {
                                const sheets = [exportSheets.tableRows, exportSheets.periodRows];
                                const sheetNames = ["AUM (MSEK)", "Periods"];
                                if (exportSheets.dailyRows.length > 0) {
                                    sheets.push(exportSheets.dailyRows);
                                    sheetNames.push("Daily AUM (MSEK)");
                                }
                                exportToXlsx(sheets, `aum-quarterly-${endDate}.xlsx`, sheetNames);
                            }}
                        >
                            <FileSpreadsheet />
                        </button>
                    )}
                    <button
                        className="export-link"
                        type="button"
                        title="Save as PNG"
                        onClick={async () => {
                            const title = titleRef.current;
                            const table = containerRef.current?.querySelector("table");
                            if (!title || !table) return;

                            const padding = 16;
                            const gap = 12;
                            const tableRect = table.getBoundingClientRect();
                            const width = tableRect.width + padding * 2;
                            const height = title.getBoundingClientRect().height + gap + tableRect.height + padding * 2;

                            // Clone the title and the table with their computed styles, so the image looks like the screen without the page's stylesheets
                            const wrapper = document.createElement("div");
                            wrapper.setAttribute("style", `width:${tableRect.width}px;padding:${padding}px;background:#fff`);
                            for (const source of [title, table]) {
                                const clone = source.cloneNode(true) as HTMLElement;
                                const originalElements = [source, ...Array.from(source.querySelectorAll("*"))];
                                const clonedElements = [clone, ...Array.from(clone.querySelectorAll("*"))] as HTMLElement[];
                                originalElements.forEach((element, index) => {
                                    const computed = getComputedStyle(element);
                                    clonedElements[index].style.cssText = Array.from(computed)
                                        .map((property) => `${property}:${computed.getPropertyValue(property)}`)
                                        .join(";");
                                });
                                wrapper.appendChild(clone);
                            }
                            const titleClone = wrapper.firstElementChild as HTMLElement;
                            titleClone.style.width = `${tableRect.width}px`;
                            titleClone.style.margin = `0 0 ${gap}px`;

                            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><foreignObject width="100%" height="100%">${new XMLSerializer().serializeToString(wrapper)}</foreignObject></svg>`;

                            const image = new Image();
                            image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
                            await image.decode();

                            // Twice the resolution to keep the text sharp
                            const scale = 2;
                            const canvas = document.createElement("canvas");
                            canvas.width = width * scale;
                            canvas.height = height * scale;
                            const context = canvas.getContext("2d");
                            context.fillStyle = "#fff";
                            context.fillRect(0, 0, canvas.width, canvas.height);
                            context.scale(scale, scale);
                            context.drawImage(image, 0, 0);
                            canvas.toBlob((blob) => saveAs(blob, `aum-quarterly-${endDate}.png`));
                        }}
                    >
                        <ImageDown />
                    </button>
                    <Popover.Root>
                        <Popover.Trigger asChild>
                            <button className="export-link" type="button" title="How to read this page">
                                <Info />
                            </button>
                        </Popover.Trigger>
                        <Popover.Content className="aum-help" side="bottom" align="end" sideOffset={6} collisionPadding={16}>
                            <h2>How to read this page</h2>
                            <p>All figures are in MSEK, as of the End date unless the header of the column has a date.</p>
                            <h3>Columns</h3>
                            <ul>
                                <li>
                                    <b>YTD</b>: from 31 December last year to the End date. <b>Current month</b>: from the end of last month
                                    to the End date.
                                </li>
                                <li>
                                    <b>Last week</b> and <b>Yesterday</b> are single days: the same weekday a week ago, and the day before
                                    the End date. Value chg. and Flow are for that day.
                                </li>
                            </ul>
                            <h3>Figures</h3>
                            <ul>
                                <li>
                                    <b>AUM</b>: the NAV after orders on the last NAV date up to the end of the period. It is only shown for
                                    Yesterday, as of that day.
                                </li>
                                <li>
                                    <b>Total chg.</b>: Value chg. plus Flow, which is the change of AUM over the period. It is shown for YTD,
                                    the current month and Last week.
                                </li>
                                <li>
                                    <b>Flow</b>: units subscribed or redeemed, valued at the NAV per unit.
                                </li>
                                <li>
                                    <b>Value chg.</b>: AUM at the end minus AUM at the start minus Flow, so it is the part that is not
                                    flows.
                                </li>
                            </ul>
                            <h3>Segment and customer</h3>
                            <ul>
                                <li>
                                    Pick a <b>Segment</b> to see the same figures for one customer group, and then a <b>Customer</b> to see
                                    a single client account. Fund overview shows all funds.
                                </li>
                                <li>
                                    For a segment, AUM is the units in the share register valued at the NAV per unit, and Flow is the
                                    settled order amount.
                                </li>
                                <li>
                                    <b>Unmapped</b> are client accounts that are not in a segment yet. A warning shows when there are any.
                                </li>
                                <li>Amounts below 1 MSEK get decimals in a segment and a customer view, so that small holdings show.</li>
                            </ul>
                            <h3>Chart and export</h3>
                            <ul>
                                <li>The chart shows daily AUM per fund from the first NAV, the total AUM and the accumulated flows.</li>
                                <li>
                                    The Excel button exports the table, the periods and the daily AUM. The image button saves the table as a
                                    PNG.
                                </li>
                            </ul>
                        </Popover.Content>
                    </Popover.Root>
                </div>
            </div>
            {!isOverview && segment !== unmappedSegment && unmappedSummary.count > 0 && (
                <div className="aum-unmapped-warning">
                    <span>
                        {unmappedSummary.count} client account{unmappedSummary.count === 1 ? " is" : "s are"} not mapped to a segment (
                        {numberFormats[2].format(unmappedSummary.aum)} MSEK AUM on {endDate}). The segments do not include{" "}
                        {unmappedSummary.count === 1 ? "it" : "them"}.
                    </span>
                    <button type="button" onClick={() => setSegment(unmappedSegment)}>
                        Show Unmapped
                    </button>
                </div>
            )}
            {content}
            {!isOverview && !fetching && !error && !fetchingRegister && !errorRegister && (
                <div className="aum-orders">
                    <p className="aum-orders-title">
                        Latest subscriptions and redemptions, {ordersDays} days up to {endDate}
                    </p>
                    {fetchingOrders && <p>Loading orders</p>}
                    {errorOrders && <p>error: {JSON.stringify(errorOrders, null, 2)}</p>}
                    {!fetchingOrders && !errorOrders && orderRows.length === 0 && <p>No orders in the period.</p>}
                    {!fetchingOrders && !errorOrders && orderRows.length > 0 && (
                        <>
                            <ReactTable columns={orderColumns} data={orderRows} initialPageSize={1000} />
                            <p className="aum-orders-note">
                                A settled order has its settled amount. * An order in units that is not settled yet, valued at the NAV per
                                unit of the last NAV up to the trade date. The amount in SEK uses the exchange rate of that NAV. An order
                                without a NAV has no amount.
                            </p>
                        </>
                    )}
                </div>
            )}
            {isOverview && !fetching && !error && (
                <div className="aum-chart">
                    {fetchingChart && <p>Loading chart</p>}
                    {errorChart && <p>error: {JSON.stringify(errorChart, null, 2)}</p>}
                    {!fetchingChart && !errorChart && (
                        <>
                            <div className="aum-chart-legend">
                                {chartSeries.map((series) => (
                                    <span key={series.name}>
                                        <i
                                            className={series.dash ? "aum-legend-dash" : undefined}
                                            style={{ backgroundColor: series.color, borderColor: series.color }}
                                        />
                                        {series.name}
                                    </span>
                                ))}
                            </div>
                            <TimeSeriesLines
                                series={chartSeries}
                                showLegend={false}
                                yFormat={(value) => numberFormats[0].format(value)}
                                yLabel="MSEK"
                                ariaLabel="AUM per fund, total AUM and accumulated flow"
                                height={480}
                                style={{ width: "100%" }}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
